/**
 * The iframe shields of a frame window's drag band (`frame-window-band.ts`),
 * kept measured while the pointer is in the window: as it moves, when an
 * iframe loads and when the window or an iframe resizes.
 */
import { useCallback, useEffect, useState, type RefObject } from "react";
import {
  frameWindowBandShields,
  sameFrameWindowBandShields,
  type FrameWindowBandShield,
} from "./frame-window-band";

const NO_SHIELDS: FrameWindowBandShield[] = [];

export interface FrameWindowBandShields {
  shields: FrameWindowBandShield[];
  /** The pointer entered the window or one of its elements. */
  onPointerOver: () => void;
  onPointerLeave: () => void;
}

export function useFrameWindowBandShields(
  rootRef: RefObject<HTMLElement | null>,
): FrameWindowBandShields {
  const [inside, setInside] = useState(false);
  const [shields, setShields] = useState(NO_SHIELDS);

  useEffect(() => {
    const root = rootRef.current;
    if (!inside || !root) return;
    let frame = 0;
    const observed = new Set<Element>();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => measure());
    // Observing an element again would report it again: each only once.
    function observe(element: Element) {
      if (!observer || observed.has(element)) return;
      observed.add(element);
      observer.observe(element);
    }
    function measure() {
      if (!root) return;
      observe(root);
      for (const iframe of root.querySelectorAll("iframe")) observe(iframe);
      const next = frameWindowBandShields(root);
      setShields((current) =>
        sameFrameWindowBandShields(current, next) ? current : next,
      );
    }
    function measureNextFrame() {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    }
    measure();
    // `load` does not bubble; a capturing listener hears every iframe's.
    root.addEventListener("load", measure, true);
    root.addEventListener("pointermove", measureNextFrame);
    root.addEventListener("pointerover", measureNextFrame);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      root.removeEventListener("load", measure, true);
      root.removeEventListener("pointermove", measureNextFrame);
      root.removeEventListener("pointerover", measureNextFrame);
      setShields(NO_SHIELDS);
    };
  }, [inside, rootRef]);

  const onPointerOver = useCallback(() => setInside(true), []);
  const onPointerLeave = useCallback(() => setInside(false), []);
  return { shields, onPointerOver, onPointerLeave };
}
